import { useState } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';

/**
 * 3-state supplier-level rollup toggle (off/partial/on) for Settings > Pricing.
 * Clicking it bulk-turns every category under that supplier on/off — with a
 * confirm dialog, per explicit user requirement ("yes bulk turn on but warn").
 * While a category is OFF, its prices are invisible to /pricing/lookup
 * (Create Quote/Pro Forma auto-fill) and Rebate Summary auto-matching.
 */
const STATUS_STYLES = {
  off: { bg: 'bg-gray-200', dot: 'bg-gray-500', text: 'text-gray-600', label: 'Off' },
  partial: { bg: 'bg-yellow-300', dot: 'bg-yellow-700', text: 'text-yellow-900', label: 'Partial' },
  on: { bg: 'bg-green-500', dot: 'bg-white', text: 'text-white', label: 'On' },
};

export const SupplierActiveToggle = ({ supplierId, supplierName, activeStatus, activeCount, totalCount, onChanged }) => {
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const nextState = activeStatus !== 'on'; // off/partial -> turn everything ON; on -> turn everything OFF
  const style = STATUS_STYLES[activeStatus] || STATUS_STYLES.off;

  const confirmAndToggle = async () => {
    setSubmitting(true);
    try {
      await api.post(`${API}/pricing/suppliers/${supplierId}/set-active`, { is_active: nextState });
      toast.success(
        nextState
          ? `Turned ON pricing for all ${totalCount} categor${totalCount === 1 ? 'y' : 'ies'} under ${supplierName}`
          : `Turned OFF pricing for all ${totalCount} categor${totalCount === 1 ? 'y' : 'ies'} under ${supplierName}`
      );
      setConfirming(false);
      onChanged();
    } catch {
      toast.error('Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setConfirming(true); }}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${style.bg} ${style.text} shrink-0`}
        title={`${activeCount}/${totalCount} categories active — visible in Create Quote/Pro Forma`}
        data-testid={`pricing-supplier-active-toggle-${supplierId}`}
      >
        <span className={`w-2 h-2 rounded-full ${style.dot}`} />
        {style.label} ({activeCount}/{totalCount})
      </button>
      <Dialog open={confirming} onOpenChange={(v) => { if (!v) setConfirming(false); }}>
        <DialogContent className="bg-white max-w-sm" data-testid="pricing-supplier-active-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{nextState ? 'Turn ON' : 'Turn OFF'} pricing for {supplierName}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            This will {nextState ? 'turn ON' : 'turn OFF'} every product group (category) under "{supplierName}"
            ({totalCount} categor{totalCount === 1 ? 'y' : 'ies'}). {nextState
              ? 'Their prices will start showing up in Create Quote / Pro Forma and Rebate Summary matching.'
              : 'Their prices will stop showing anywhere else in the system — including Create Quote / Pro Forma and Rebate Summary matching — until turned back on.'}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={submitting}>Cancel</Button>
            <Button
              className={nextState ? 'text-white bg-green-600 hover:bg-green-700' : 'text-white bg-red-600 hover:bg-red-700'}
              onClick={confirmAndToggle}
              disabled={submitting}
              data-testid="pricing-supplier-active-confirm-btn"
            >
              {submitting ? 'Applying...' : nextState ? 'Yes, turn ON all' : 'Yes, turn OFF all'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SupplierActiveToggle;
